import { useMemo } from 'react';
import * as THREE from 'three';
import { useSketchStore } from '../../state/useSketchStore';
import { SKETCH_COLORS } from '../../themes/colors';

/**
 * Sketch guide layer — Tape Measure output rendered as dashed lines and
 * guide-point markers. Guides are construction aids: they raycast nowhere,
 * draw under the solids and hide with the status band toggle.
 */

function GuideLine({ a, b }: { a: THREE.Vector3; b: THREE.Vector3 }) {
    const line = useMemo(() => {
        const geometry = new THREE.BufferGeometry().setFromPoints([a, b]);
        const material = new THREE.LineDashedMaterial({
            color: new THREE.Color(SKETCH_COLORS.guide),
            dashSize: 0.35,
            gapSize: 0.2,
            depthWrite: false,
            transparent: true,
            opacity: 0.85,
        });
        const obj = new THREE.Line(geometry, material);
        obj.computeLineDistances();
        obj.raycast = () => null;
        return obj;
    }, [a, b]);
    return <primitive object={line} />;
}

export function SketchGuideLayer() {
    const guides = useSketchStore((s) => s.guides);
    const showGuides = useSketchStore((s) => s.showGuides);

    if (!showGuides) return null;

    return (
        <group>
            {Object.values(guides).map((guide) => {
                if (guide.kind === 'line' && guide.b) {
                    return (
                        <GuideLine
                            key={guide.id}
                            a={new THREE.Vector3(...guide.a)}
                            b={new THREE.Vector3(...guide.b)}
                        />
                    );
                }
                return (
                    <mesh
                        key={guide.id}
                        position={new THREE.Vector3(...guide.a)}
                        raycast={() => null}
                    >
                        <sphereGeometry args={[0.06, 8, 8]} />
                        <meshBasicMaterial color={SKETCH_COLORS.guide} />
                    </mesh>
                );
            })}
        </group>
    );
}
